import {
  convertFilters,
  Filter,
  FilterModel,
  FilterType,
  SetFilter,
} from '@helios/server-side-util'
import {
  FlexPositionsQueryRequest,
  QueryFilter,
  QuerySort,
} from '../../models/flex-positions-query.model'
import { MATCH_STATUS_FIELD } from '../filters/match-status-chips'
import { QueryFieldMeta } from './field-types'

/**
 * ag-Grid's sort model entry, as it arrives on `IServerSideGetRowsParams.request.sortModel`.
 * Re-declared locally rather than reusing `@helios/server-side-util`'s `AgGridSortModel<T>` because
 * this contract's sort entry is a different shape on both axes that matter (uppercase direction, plus
 * a mandatory `type`), so nothing downstream is shared.
 */
export interface GridSortModelEntry {
  colId: string
  sort: 'asc' | 'desc'
}

/**
 * The identity column. Hidden in the grid but ALWAYS projected: it is `getRowNodeId`, the cross-grid
 * matching key, and the field the match-count cross-filter filters on, so a response without it
 * leaves every row unaddressable.
 */
export const ROW_ID_FIELD = 'rowId'

/**
 * The per-row action eligibility (PVAL-26401). Like {@link ROW_ID_FIELD} it is never a grid column —
 * `row.adapter` promotes it to `WorkbenchRow.availableActions` and keeps it out of `fields` — so the
 * ONLY way it can reach the response is by being projected here. A catalog-driven projection cannot
 * carry it: `projectedFields()` returns DISPLAYED columns, so even if the backend published it as a
 * catalog column it would have to be visible in the grid to be asked for.
 */
export const AVAILABLE_ACTIONS_FIELD = 'availableActions'

/**
 * Whether the flex-positions query emits `availableActions` yet.
 *
 * Currently `false`. PVAL-26401 is the backend half of PVAL-26403 and has not landed for this
 * service — `FlexPositionEntity.FIELD_PROPERTIES` lists rowId / accountId / reconDate / matchStatus
 * / matchCounts / matchedIds / posData and nothing else. Naming a field that entity does not know
 * goes through `SqlRequestImpl.create`'s validation, so projecting it early risks a 400 that kills
 * the whole grid — strictly worse than the gating being inert.
 *
 * FLIP TO `true` in the same MR that confirms PVAL-26401 is on master. Nothing else has to change:
 * the reader (`row.adapter.parseAvailableActions`), the normaliser (`utils/available-actions.ts`)
 * and all four menu gates are already built and covered. Verify the field name against
 * `FIELD_PROPERTIES` when you flip it — a mismatch fails the same way, as a 400.
 *
 * Same pattern, and the same reason, as `POSITION_ACTIONS_ENDPOINT_AVAILABLE`: positions-2 has no
 * feature-flag mechanism, so a pending-backend seam is a module constant.
 */
export const AVAILABLE_ACTIONS_PROJECTED = false

/**
 * Fields projected on EVERY request regardless of which columns are on screen, because the UI needs
 * them to function rather than to display.
 */
export const MANDATORY_QUERY_FIELDS: readonly string[] = [
  ROW_ID_FIELD,
  ...(AVAILABLE_ACTIONS_PROJECTED ? [AVAILABLE_ACTIONS_FIELD] : []),
]

export interface QueryModelInput {
  /** Inclusive, from ag-Grid's block request. */
  startRow: number
  /** Exclusive, from ag-Grid's block request. */
  endRow: number
  /** ag-Grid's filter model, keyed by colId. */
  filterModel: FilterModel<unknown>
  /** ag-Grid's sort model, in user-chosen precedence order. */
  sortModel: readonly GridSortModelEntry[]
  /**
   * The colIds to project, in grid order — normally the visible columns. Names this map does not
   * know are dropped (see {@link buildQueryRequest}).
   */
  projection: readonly string[]
  /** Per-field query metadata for the source's catalog, from `buildFieldMeta`. */
  meta: ReadonlyMap<string, QueryFieldMeta>
  /** The active match-count cross-filter's row ids, or `null` when none is active. */
  crossFilterIds: readonly string[] | null
}

const setFilter = (values: readonly string[]): SetFilter => ({
  filterType: FilterType.Set,
  values: [...values],
})

/**
 * Translate ag-Grid's filter + sort model into a POST /flex-positions/query body.
 *
 * The whole request is derived here, in one pure function, precisely so the request BODY is what
 * tests assert against — a silent row loss in this translation is indistinguishable from an empty
 * account at every layer above it.
 *
 * Three rules are worth reading before changing anything:
 *
 *  1. **A field this builder does not recognise never reaches the request.** The grid carries columns
 *     no backend knows — the four `calc:` unit differences, the computed Helios columns, the
 *     match-count columns the UI keys by target (`matchCount:cust`) — and naming one in `fields`,
 *     `filter` or `sort` is a 400. `meta` is the allow-list.
 *
 *  2. **The cross-filter owns the whole predicate.** `matchStatus` needs no special handling — the
 *     status chips ARE that column's filter (see match-status-chips.ts), so it arrives through
 *     `filterModel` like any other column. An active cross-filter then substitutes a `rowId` entry for
 *     the `matchStatus` one entirely — clicking a match count means "show me exactly these rows",
 *     which the toolbar must not narrow further.
 *
 *  3. **`context.fieldStorage.aggInfo` covers `fields` + `filter` + `sort`,** not just the
 *     projection. It is a hint that saves the backend a Redis lookup, so an incomplete list is a
 *     performance bug rather than a wrong answer — but it is cheap to get right here and expensive to
 *     debug later.
 */
export function buildQueryRequest(
  input: QueryModelInput,
): FlexPositionsQueryRequest {
  const { meta } = input

  const filter = buildFilterMap(input)
  const sort = buildSort(input)
  const fields = buildFields(input)

  return {
    startRow: input.startRow,
    endRow: input.endRow,
    fields,
    filter,
    sort,
    context: {
      fieldStorage: {
        aggInfo: aggInfoFields(
          [
            ...fields,
            ...Object.keys(filter),
            ...sort.map(entry => entry.field),
          ],
          meta,
        ),
      },
    },
  }
}

/** Rule 1 + rule 2, in that order. */
function buildFilterMap(input: QueryModelInput): Record<string, QueryFilter> {
  const { meta, filterModel, crossFilterIds } = input

  const converted = convertFilters(filterModel) as Record<string, Filter>
  let filter: Record<string, QueryFilter> = {}
  Object.entries(converted).forEach(([field, entry]) => {
    if (!meta.has(field)) return
    // The converted entry's own `filterType` is kept rather than restamped from `meta`: it describes
    // the SHAPE that was actually built (a `set` entry has `values`, a scalar one has `filters[]`),
    // and the grid's filter for the column was itself derived from the same resolved type, so the
    // two only ever disagree where the shape is what the backend has to read. It is always present —
    // `convertFilters` cannot emit an entry without one — which is the contract's real requirement.
    filter[field] = entry
  })

  // The status chips arrive through `filterModel` like any other column filter — they ARE the
  // `matchStatus` column's filter (see match-status-chips.ts), so there is nothing to merge here and
  // no way for a header filter and the chips to disagree. An all-chips-on state clears that column's
  // filter, so no `matchStatus` entry exists to copy: omitting it is what "do not constrain this
  // column" means, where enumerating every value would drop rows whose status is absent or is a value
  // this UI has not enumerated.

  if (crossFilterIds) {
    // Clicking a match count means "show me exactly these rows", so the cross-filter wins outright
    // over the chips rather than narrowing further. Substituting for the `matchStatus` entry — rather
    // than sending both — is what makes that true. Clearing the cross-filter restores the entry with
    // no extra work: this map is re-derived from the grid's live filter model on every request, and
    // the chips' own filter was never touched.
    filter = withoutKey(filter, MATCH_STATUS_FIELD)
    filter[ROW_ID_FIELD] = setFilter(crossFilterIds)
  }

  return filter
}

/** A copy of `record` without `key`. */
const withoutKey = <T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> =>
  Object.fromEntries(Object.entries(record).filter(([name]) => name !== key))

function buildSort(input: QueryModelInput): QuerySort[] {
  const { meta, sortModel } = input
  const sort: QuerySort[] = []
  sortModel.forEach(entry => {
    const fieldMeta = meta.get(entry.colId)
    if (!fieldMeta) return
    sort.push({
      field: entry.colId,
      direction: entry.sort === 'desc' ? 'DESC' : 'ASC',
      type: fieldMeta.sortType,
    })
  })
  return sort
}

/**
 * The projection: the mandatory identity column first, then the requested columns the catalog knows,
 * de-duplicated and order-stable.
 */
function buildFields(input: QueryModelInput): string[] {
  const { meta, projection } = input
  const fields: string[] = []
  const claimed = new Set<string>()

  const push = (field: string): void => {
    if (claimed.has(field)) return
    claimed.add(field)
    fields.push(field)
  }

  MANDATORY_QUERY_FIELDS.forEach(push)
  projection.forEach(field => {
    if (meta.has(field)) push(field)
  })

  return fields
}

/** Rule 3: every AGG_INFO-storage field the request touches, de-duplicated and order-stable. */
function aggInfoFields(
  touched: readonly string[],
  meta: ReadonlyMap<string, QueryFieldMeta>,
): string[] {
  const aggInfo: string[] = []
  const claimed = new Set<string>()
  touched.forEach(field => {
    if (claimed.has(field)) return
    if (meta.get(field)?.storage !== 'AGG_INFO') return
    claimed.add(field)
    aggInfo.push(field)
  })
  return aggInfo
}
